import { Injectable } from '@angular/core';
import { PermissionsService, ProjectStore, ReleaseStore, SessionRepository, SystemStore } from '@frontend-site/shared/core';
import { combineLatest, map, Observable } from 'rxjs';
import { MainHeaderMenu, MenuItem, MenuStates } from '../../models';
import { DefaultMainHeaderMenu } from '../../models/default-main-header-menu';
import { MenuBuilderService } from './menu-builder';
import { MENU_IDS } from '../menu-ids';
import { Project, ProjectType, Release, System, SystemDetails, SystemTypes, ApplicationConfig } from '@frontend-site/shared/data';

@Injectable()
export class AllProjectMenuBuilderService implements MenuBuilderService {
    get menuState(): MenuStates {
        return MenuStates.ALL_PROJECTS;
    }

    constructor(
        private readonly systemStore: SystemStore,
        private readonly projectStore: ProjectStore,
        private readonly releaseStore: ReleaseStore,
        private readonly sessionRepository: SessionRepository,
        private readonly permissionsService: PermissionsService
    ) {}

    build$(): Observable<MainHeaderMenu | null> {
        return combineLatest([
            this.systemStore.systems$,
            this.projectStore.projects$,
            this.releaseStore.releases$,
            this.sessionRepository.applicationConfig$,
            this.systemStore.systemsDetails$
        ]).pipe(
            map(([systems, projects, releases, appConfig, systemsDetails]) => {
                const overviewSubMenu: MenuItem[] = this.createOverviewSubMenuItem(projects, systems, systemsDetails, releases, appConfig);
                const menu: MenuItem[] = [
                    {
                        id: MENU_IDS.ALL_PROJECTS.OVERVIEW.DEFAULT,
                        label: 'Enumerations.ApplicationModules.OVERVIEW_MENU.OVERVIEW',
                        routePath: '/project',
                        active: true,
                        menu: overviewSubMenu
                    }
                ];

                if (this.hasPortfolioPermission() && appConfig?.rdxEnabled) {
                    menu.push({
                        id: MENU_IDS.ALL_PROJECTS.PORTFOLIO,
                        label: 'Layouts.Masthead.COL_MENU.COLUMN_HEADER.PORTFOLIO',
                        routePath: `/releaseDynamix/${appConfig?.companyId}/portfolio/requirements/list`
                    });
                }

                if (this.hasGlobalDefectsPermission()) {
                    menu.push({
                        id: MENU_IDS.ALL_PROJECTS.DEFECTS,
                        label: 'Layouts.Masthead.COL_MENU.COLUMN_HEADER.DEFECTS',
                        routePath: `/global/${appConfig?.companyId}/defects`
                    });
                }
                return new DefaultMainHeaderMenu(this.menuState, {
                    showSearch: false,
                    items: menu,
                    title: appConfig?.companyName ?? ''
                });
            })
        );
    }

    private createOverviewSubMenuItem(
        projects: Project[],
        systems: System[],
        systemsDetails: SystemDetails[],
        releases: Release[] | null,
        appConfig?: ApplicationConfig
    ): MenuItem[] {
        const projectSubMenu: MenuItem[] = [];
        projectSubMenu.push({
            id: MENU_IDS.ALL_PROJECTS.OVERVIEW.PROJECTS,
            label: 'Layouts.Masthead.PROJECT',
            routePath: '/project'
        });

        if (appConfig?.rdxEnabled && releases) {
            projectSubMenu.push({
                id: MENU_IDS.ALL_PROJECTS.OVERVIEW.RELEASE,
                label: 'Layouts.Masthead.COL_MENU.COLUMN_HEADER.RELEASES',
                routePath: `/releaseDynamix/${appConfig.companyId}/contextSelector/chart`
            });
        }

        this.addSubMenuSystems(projectSubMenu, systems, systemsDetails, appConfig);
        this.addDashboardSubMenu(projectSubMenu, projects, systems, appConfig);

        return projectSubMenu;
    }

    private addSubMenuSystems(
        projectSubMenu: MenuItem[],
        systems: System[],
        systemsDetails: SystemDetails[],
        appConfig?: ApplicationConfig
    ): void {
        const filteredSystems = systems.find((system) => {
            const systemDetails = systemsDetails.find((s) => s.systemId === system.id);
            if (system?.accountType === SystemTypes.SALESFORCE) return true;
            if (appConfig?.uatOnly) return false;
            return (
                appConfig?.iaEnabled ||
                appConfig?.rdxEnabled ||
                (systemDetails && systemDetails.systemEtlExist && !systemDetails.systemDummyEtl && !appConfig?.rdxEnabled)
            );
        });
        if (filteredSystems) {
            projectSubMenu.push({
                id: MENU_IDS.ALL_PROJECTS.OVERVIEW.SYSTEM,
                label: 'Layouts.Masthead.COL_MENU.COLUMN_HEADER.SYSTEMS',
                routePath: '/systems'
            });
        }
    }

    private addDashboardSubMenu(projectSubMenu: MenuItem[], projects: Project[], systems: System[], appConfig?: ApplicationConfig): void {
        const nonSalesforceSystems = systems.filter(
            (erp) => erp.companyId === appConfig?.companyId && erp.accountType !== SystemTypes.SALESFORCE
        );
        if (nonSalesforceSystems.length > 0) {
            const recentlyUsedProjects = projects?.filter((p) => p.companyId === appConfig?.companyId);
            const hasNonSapProjects =
                recentlyUsedProjects?.length === 0 ||
                recentlyUsedProjects.some(
                    (p) =>
                        p.projectType !== ProjectType.S4_CONVERT &&
                        p.projectType !== ProjectType.S4_ACCELERATE &&
                        p.projectType !== ProjectType.ICR_3
                );

            if (hasNonSapProjects) {
                projectSubMenu.push({
                    id: MENU_IDS.ALL_PROJECTS.OVERVIEW.DASHBOARD,
                    label: 'Layouts.Masthead.DASHBOARD',
                    routePath: '/dashboard/projectManager'
                });
            }
        }
    }

    private hasPortfolioPermission(): boolean {
        return this.permissionsService.hasPermissions('Portfolio : view');
    }

    private hasGlobalDefectsPermission() {
        return this.permissionsService.hasPermissions('Cross project defect : view');
    }
}
